import { useParams, useNavigate } from 'react-router-dom';
import { useAppState } from '@/hooks/useAppState';
import TaskItem from '@/components/feature/TaskItem';
import StatusTag from '@/components/feature/StatusTag';
import PrivacyBadge from '@/components/feature/PrivacyBadge';
import EmptyState from '@/components/feature/EmptyState';

const stageConfig: Record<string, { icon: string; bg: string; color: string }> = {
  '规划': { icon: 'ri-compasses-line', bg: 'bg-secondary-100', color: 'text-secondary-600' },
  '执行': { icon: 'ri-play-circle-line', bg: 'bg-primary-100', color: 'text-primary-600' },
  '等待': { icon: 'ri-pause-circle-line', bg: 'bg-amber-100', color: 'text-amber-600' },
  '复盘': { icon: 'ri-refresh-line', bg: 'bg-accent-100', color: 'text-accent-600' },
  '完成': { icon: 'ri-check-double-line', bg: 'bg-accent-100', color: 'text-accent-700' },
};

export default function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tasks, projects, toggleTask } = useAppState();

  const project = projects.find((p) => p.id === id);

  if (!project) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <p className="text-foreground-500">项目未找到</p>
      </div>
    );
  }

  const projectTasks = tasks.filter((t) => t.projectId === project.id);
  const activeTasks = projectTasks.filter((t) => !t.completed);
  const doneTasks = projectTasks.filter((t) => t.completed);
  const stage = stageConfig[project.stage] || stageConfig['规划'];

  return (
    <div className="min-h-screen bg-background-50 pb-24">
      {/* Header */}
      <header className="px-4 pt-6 pb-4"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 24px)' }}>
        <div className="flex items-center gap-3 mb-3">
          <button
            onClick={() => navigate('/projects')}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-lg text-foreground-600"></i>
          </button>
          <div className="flex-1 flex items-center gap-2">
            <h1 className="text-base font-bold text-foreground-800 font-heading">{project.name}</h1>
            <PrivacyBadge type="private" />
          </div>
        </div>

        {/* Project cover */}
        <div className={`rounded-xl p-5 ${stage.bg}`}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${stage.color} bg-background-50`}>
                  <i className={stage.icon}></i>
                </span>
                <StatusTag status={project.stage} type="stage" />
              </div>
              <p className="text-xs text-foreground-600 mt-2 leading-relaxed">{project.goal}</p>
            </div>
          </div>

          {/* Progress */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-foreground-500">整体进度</span>
              <span className="text-[10px] font-semibold text-foreground-700">{project.progress}%</span>
            </div>
            <div className="w-full h-2 bg-background-50 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 bg-primary-500"
                style={{ width: `${project.progress}%` }}
              ></div>
            </div>
          </div>
        </div>
      </header>

      <main className="px-4 space-y-4">
        {/* Next step - highlighted */}
        {project.nextStep && (
          <div className="bg-accent-50 rounded-xl px-4 py-3.5 border border-accent-200/40">
            <p className="text-[10px] text-accent-600 font-medium mb-1">下一步行动</p>
            <div className="flex items-start gap-2">
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-arrow-right-circle-fill text-accent-500"></i>
              </span>
              <p className="text-sm text-accent-700 font-medium leading-relaxed">{project.nextStep}</p>
            </div>
          </div>
        )}

        {/* Meta info */}
        <div className="flex items-center gap-4 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-calendar-line"></i>
            </span>
            截止：{project.deadline}
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-task-line"></i>
            </span>
            {activeTasks.length} 项待办
          </span>
        </div>

        {/* Project tasks */}
        <section>
          <h2 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">项目待办</h2>
          <div className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden">
            {activeTasks.length > 0 ? (
              <div className="divide-y divide-background-200/50">
                {activeTasks.map((task) => (
                  <TaskItem
                    key={task.id}
                    task={task}
                    onToggle={toggleTask}
                    compact
                  />
                ))}
              </div>
            ) : doneTasks.length > 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-xs text-foreground-400">全部完成！</p>
              </div>
            ) : (
              <div className="px-4 py-6 text-center">
                <p className="text-xs text-foreground-400">还没有项目待办，点击下方按钮添加</p>
              </div>
            )}

            {doneTasks.length > 0 && (
              <>
                <div className="px-4 py-2 border-t border-background-200/50">
                  <p className="text-[10px] text-foreground-400">已完成 ({doneTasks.length})</p>
                </div>
                <div className="divide-y divide-background-200/50">
                  {doneTasks.map((task) => (
                    <TaskItem
                      key={task.id}
                      task={task}
                      onToggle={toggleTask}
                      compact
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* Updates / Review */}
        {project.updates.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">更新记录</h2>
            <div className="space-y-2">
              {project.updates.map((update, i) => (
                <div key={i} className="bg-background-50 rounded-lg px-3.5 py-3 border border-background-200/40">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400"></span>
                    <span className="text-[10px] text-foreground-400">{update.date}</span>
                  </div>
                  <p className="text-xs text-foreground-600 leading-relaxed">{update.content}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Future expansion area */}
        <section className="pb-2">
          <h2 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">扩展</h2>
          <div className="grid grid-cols-3 gap-2">
            {['项目资料', '预算', '协作者', '里程碑'].map((item) => (
              <button
                key={item}
                className="text-center py-3 rounded-lg bg-background-100 border border-background-200/40 text-[10px] text-foreground-400 font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-5 h-5 flex items-center justify-center mx-auto mb-1">
                  <i className={`ri-${
                    item === '项目资料' ? 'file-text-line' :
                    item === '预算' ? 'money-cny-circle-line' :
                    item === '协作者' ? 'team-line' : 'flag-line'
                  } text-sm`}></i>
                </span>
                {item}
              </button>
            ))}
          </div>
        </section>
      </main>

      {/* Fixed bottom button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background-50 via-background-50 to-transparent z-10"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
        <button className="w-full py-3 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          新增项目待办
        </button>
      </div>
    </div>
  );
}
